import { TranslatedTextField } from "shared/localizedText";
import { LocalizedText, PRIMARY_LOCALE } from "shared/localizedText";
import React, { useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { Link, useHistory } from "react-router-dom";
import { Area } from "types";
import ProgressButton from "shared/ProgressButton";
import { useCreateTemplate } from "routes/createContent/api/useCreateTemplate";
import { useUpdateTemplate } from "routes/createContent/api/useUpdateTemplate";
import { TemplateFormData, formToUpsertBody } from "./types";
import {
  EXERCISES_PER_DAY_OPTIONS,
  GROUP_OPTIONS,
  HEALTH_FORM_TYPE_OPTIONS,
  MAX_DURATION_WEEKS,
  humanize,
} from "./constants";
import ExercisesSection from "./components/ExercisesSection";
import LessonsSection from "./components/LessonsSection";

type Props = {
  initialValues: TemplateFormData;
  readOnly?: boolean;
};

const TemplateEditor: React.VFC<Props> = ({ initialValues, readOnly = false }) => {
  const history = useHistory();
  // shouldUnregister: false keeps non-Controller fields (ids, per-item
  // title/image/duration metadata) in the form value so getValues() returns the
  // full shape — same reasoning as the lesson editor.
  const form = useForm<TemplateFormData>({ defaultValues: initialValues, shouldUnregister: false });
  const { control, register, handleSubmit, getValues } = form;
  const create = useCreateTemplate();
  const update = useUpdateTemplate();
  const isEditing = initialValues.id != null;
  const saving = create.isLoading || update.isLoading;
  const saveError = create.error || update.error;
  const saveErrorMessage =
    (saveError as { response?: { status?: number } } | null)?.response?.status === 409
      ? "That key is already taken by another template."
      : "Failed to save template.";

  useEffect(() => {
    register("id");
  }, [register]);

  const ongoing = useWatch({ control, name: "ongoing" }) as boolean;
  const name = useWatch({ control, name: "name" }) as LocalizedText;
  const group = useWatch({ control, name: "group" }) as string;
  const exercises = useWatch({ control, name: "exercises" }) as unknown[] | undefined;
  const key = useWatch({ control, name: "key" }) as string | null;
  const canSave = !!name?.[PRIMARY_LOCALE.code]?.trim() && !!group && !!key && (exercises?.length ?? 0) > 0;

  const onSubmit = handleSubmit(async () => {
    const data = getValues();
    const body = formToUpsertBody(data);
    if (!body) {
      return;
    }
    try {
      if (isEditing && data.id != null) {
        await update.mutateAsync({ id: data.id, body });
      } else {
        await create.mutateAsync(body);
      }
      history.push(Area.CreateContent_Templates);
    } catch {
      // Keep the current form state so the user can retry.
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={onSubmit} style={{ paddingRight: 24 }}>
        <h1>{readOnly ? "View template" : isEditing ? "Edit template" : "New template"}</h1>

        <Box display="flex" flexDirection="column" style={{ gap: 16, maxWidth: 520 }}>
          <Controller
            control={control}
            name="name"
            render={({ onChange, value }) => (
              <TranslatedTextField label="Template name" value={value} onChange={onChange} disabled={readOnly} />
            )}
          />

          <Controller
            control={control}
            name="key"
            render={({ onChange, value }) => (
              <TextField
                label="Key"
                variant="outlined"
                size="small"
                required
                fullWidth
                disabled={readOnly || isEditing}
                value={value ?? ""}
                onChange={e => onChange(e.target.value)}
                helperText={
                  isEditing
                    ? "The template's permanent identifier. It cannot be changed."
                    : "Permanent identifier the rest of the system looks this template up by. Must be unique."
                }
              />
            )}
          />

          <Controller
            control={control}
            name="group"
            render={({ onChange, value }) => (
              <FormControl variant="outlined" size="small" required fullWidth disabled={readOnly}>
                <InputLabel>Group</InputLabel>
                <Select label="Group" value={value} onChange={e => onChange(e.target.value)}>
                  {GROUP_OPTIONS.map(g => (
                    <MenuItem key={g} value={g}>
                      {humanize(g)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <Controller
            control={control}
            name="health_form_type"
            render={({ onChange, value }) => (
              <FormControl variant="outlined" size="small" fullWidth disabled={readOnly}>
                <InputLabel>Questionnaire (health form)</InputLabel>
                <Select label="Questionnaire (health form)" value={value} onChange={e => onChange(e.target.value)}>
                  {HEALTH_FORM_TYPE_OPTIONS.map(h => (
                    <MenuItem key={h} value={h}>
                      {humanize(h)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <Controller
            control={control}
            name="exercises_per_day"
            render={({ onChange, value }) => (
              <FormControl variant="outlined" size="small" fullWidth disabled={readOnly}>
                <InputLabel>Exercises per day</InputLabel>
                <Select label="Exercises per day" value={value} onChange={e => onChange(Number(e.target.value))}>
                  {EXERCISES_PER_DAY_OPTIONS.map(n => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <Box display="flex" alignItems="center" style={{ gap: 16 }}>
            <Controller
              control={control}
              name="ongoing"
              render={({ onChange, value }) => (
                <FormControlLabel
                  control={
                    <Checkbox checked={!!value} disabled={readOnly} onChange={e => onChange(e.target.checked)} />
                  }
                  label="Ongoing program"
                />
              )}
            />
            {!ongoing && (
              <Controller
                control={control}
                name="duration_weeks"
                render={({ onChange, value }) => (
                  <TextField
                    label="Program length (weeks)"
                    type="number"
                    variant="outlined"
                    size="small"
                    disabled={readOnly}
                    inputProps={{ min: 1, max: MAX_DURATION_WEEKS }}
                    value={value}
                    onChange={e => onChange(Number(e.target.value))}
                    style={{ width: 200 }}
                  />
                )}
              />
            )}
          </Box>

          <Controller
            control={control}
            name="includes_functional_test"
            render={({ onChange, value }) => (
              <FormControlLabel
                control={<Checkbox checked={!!value} disabled={readOnly} onChange={e => onChange(e.target.checked)} />}
                label="Includes functional test"
              />
            )}
          />
        </Box>

        <ExercisesSection readOnly={readOnly} />
        <LessonsSection readOnly={readOnly} />

        <Box display="flex" alignItems="center" style={{ gap: 12, marginTop: 24 }}>
          {readOnly ? (
            initialValues.id != null && (
              <Button
                variant="contained"
                color="primary"
                component={Link}
                to={Area.CreateContent_Templates_Edit.replace(":id", String(initialValues.id))}
              >
                Edit template
              </Button>
            )
          ) : (
            <ProgressButton type="submit" loading={saving} disabled={saving || !canSave}>
              {isEditing ? "Save template" : "Create template"}
            </ProgressButton>
          )}
          <Button variant="outlined" component={Link} to={Area.CreateContent_Templates}>
            Back to list
          </Button>
        </Box>

        {!readOnly && !canSave && (
          <Box mt={1} color="text.secondary">
            A template needs an English (US) name, a key, a group and at least one exercise.
          </Box>
        )}
        {saveError && (
          <Box mt={1} color="error.main">
            {saveErrorMessage}
          </Box>
        )}
      </form>
    </FormProvider>
  );
};

export default TemplateEditor;
